<template>
  <family-page-wrapper class="family-members">
    <router-link
      class="btn btn-purple btn-round"
      :to="'/family/add-family-member'"
    >
      ADD A FAMILY MEMBER
    </router-link>

    <h5 class="sub-section-title mt-3 text-center">
      Your Family Members
    </h5>

    <div v-if="isEmptyWarningShow">
      <p>Your family member list is empty.</p>
      <p>Add a family member to share access to your account.</p>
    </div>

    <template v-else>
      <p class="text-bold text-center font-italic">
        Here's the list of the family members, loved ones, or trusted people you have authorized on your account.
      </p>

      <div class="custom-collapse pb-4">
        <el-collapse>
          <el-collapse-item
            v-for="(user, index) in familyMembers"
            :key="index"
            :name="index + 1"
          >
            <template #title>
              <div class="collapse-title">
                <span class="left-text"> {{ user.prefix }} {{ user.firstName }} {{ user.lastName }} </span>
                <span class="right-text">
                  {{ user.relationship }}
                </span>
              </div>
            </template>

            <div class="flex-container pl-20 pr-20">
              <div class="single-container">
                <table>
                  <tbody>

                    <tr>
                      <td><b>Name:</b></td>
                      <td>{{ user.prefix }} {{ user.firstName }} {{ user.lastName }}</td>
                      <td><b>Relationship:</b></td>
                      <td>{{ user.relationship }}</td>
                    </tr>
                    <tr>
                      <td><b>Mobile:</b></td>
                      <td>{{ user.phoneNumber }}</td>
                      <td><b>Email:</b></td>
                      <td>{{ user.email }}</td>
                    </tr>
                    <tr>
                      <td><b>Do Not Receive Text Updates:</b></td>
                      <td>{{ user.transactionalSms ? 'Yes' : 'No' }}</td>
                      <td>&nbsp;</td>
                      <td>&nbsp;</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <hr>

            <div class="text-center">
              <router-link
                class="btn btn-purple btn-round btn-edit text-capitalize"
                :to="`/family/add-family-member?id=${user.id}`"
              >
                Edit
              </router-link>

              <p-button
                type="yellow"
                round
                @click="removeLovedOne(user, index)"
              >
                Delete
              </p-button>
            </div>
          </el-collapse-item>
        </el-collapse>
      </div>
    </template>
  </family-page-wrapper>
</template>

<script setup lang="ts">
import { ref, Ref, computed } from 'vue';
import { ElLoading } from 'element-plus';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { LovedOne } from '@/models/lovedOne';
import { lovedOneService } from '@/services/lovedOne';

const { customerInfo } = useCustomer();

const { showConfirmPopupDialog, showSuccessPopupDialog, showErrorPopupDialog } = useSwalDialog();

const familyMembers: Ref<LovedOne[]> = ref([]);

const isEmptyWarningShow = computed(() => familyMembers.value?.length === 0);

const getFamilyMembers = async () => {
  familyMembers.value = [];

  const loading = ElLoading.service({
    lock: true,
    text: 'Loading Family Members...',
    background: 'rgba(0, 0, 0, 0.7)',
    customClass: 'custom-full-spinner-class',
  });

  try {
    const lovedOnes = await lovedOneService.fetchLovedOnes(customerInfo.value.id);
    familyMembers.value = lovedOnes.rows.map((familyMember: LovedOne) => familyMember);
  } catch (error) {
    familyMembers.value = [];
  }

  loading.close();
};

const removeLovedOne = async (lovedOne: LovedOne, index: number) => {
  const isRemoveLovedOneConfirmed = await showConfirmPopupDialog({
    text: `
      Are you sure you want to remove
      ${lovedOne.firstName} ${lovedOne.lastName}
      from your list of authorized users?
    `,
    title: 'Delete family member?',
    confirmButtonText: 'Delete',
  });

  if (!isRemoveLovedOneConfirmed) {
    return;
  }

  const loading = ElLoading.service({
    lock: true,
    text: 'Deleting Family Member...',
    background: 'rgba(0, 0, 0, 0.7)',
    customClass: 'custom-full-spinner-class',
  });

  try {
    await lovedOneService.deleteLovedOne({
      lovedOneId: familyMembers.value[index].id,
      callerId: customerInfo.value.id,
    });

    familyMembers.value.splice(index, 1);

    showSuccessPopupDialog({
      title: 'Deleted!',
      text: 'The family member has been removed.',
    });
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }

  loading.close();
};

getFamilyMembers();
</script>

<style lang="scss">
.family-members {
  .btn-edit {
    min-width: 120px;
  }
}
</style>
